/**
 * Menu Configuration Override Component
 * Permette di configurare override menu-specifici per una company
 */
import { useState, useEffect } from 'react';
import { Save01, RefreshCw01, Trash01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Toggle } from '@/components/base/toggle/toggle';
import { toast } from '@/lib/toast-service';
import { apiClient } from '@/lib/api-client';
import { useTranslation } from 'react-i18next';

interface MenuItemConfig {
    key: string;
    enabled: boolean;
    label: string;
    isOverridden?: boolean;
}

interface MenuConfigurationOverrideProps {
    companyId: string;
    companyType: string;
}

// Menu items che non possono essere disabilitati
const NON_MODIFIABLE_MENU_ITEMS = ['controlHub', 'settings', 'activity'];

const MENU_ITEMS_LABELS: Record<string, string> = {
    dashboard: 'Dashboard',
    containers: 'Containers',
    quotes: 'Quotes',
    documents: 'Documents',
    receivers: 'Business Partners',
    shippers: 'Shippers',
    orders: 'Orders',
    activity: 'Activity',
    team: 'Team',
    addressBook: 'Address Book',
    calendar: 'Calendar',
    settings: 'Settings'
};

export function MenuConfigurationOverride({ companyId, companyType }: MenuConfigurationOverrideProps) {
    const { t } = useTranslation(['pages:companies', 'common']);
    const [menuItems, setMenuItems] = useState<MenuItemConfig[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [hasOverride, setHasOverride] = useState(false);

    useEffect(() => {
        if (companyId) {
            loadMenuConfiguration();
        }
    }, [companyId]);

    const loadMenuConfiguration = async () => {
        setIsLoading(true);
        try {
            // Carica la configurazione attiva (con override se esiste)
            const activeData = await apiClient<{ items: Record<string, boolean> }>(
                `/menu-configurations/by-company/${companyId}`,
                { requireAuth: true }
            );

            // Verifica se ha un override
            try {
                const overrideCheck = await apiClient<{ has_override: boolean }>(
                    `/menu-configurations/company/${companyId}/has-override`,
                    { requireAuth: true }
                );
                setHasOverride(overrideCheck.has_override);
            } catch {
                // Se l'endpoint non esiste o fallisce, assume che non ci sia override
                setHasOverride(false);
            }

            // Converte l'oggetto in array di MenuItemConfig
            // Assicurati che i menu items non modificabili siano sempre enabled
            const items: MenuItemConfig[] = Object.entries(activeData.items).map(([key, enabled]) => ({
                key,
                enabled: NON_MODIFIABLE_MENU_ITEMS.includes(key) ? true : (enabled as boolean),
                label: MENU_ITEMS_LABELS[key] || key,
                isOverridden: false // Verrà aggiornato se carichiamo l'override
            }));

            setMenuItems(items);
        } catch (error) {
            console.error('Error loading menu configuration:', error);
            toast.error(t('menuConfig.override.loadError'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleMenuItem = (key: string) => {
        // Non permettere di disabilitare menu items non modificabili
        if (NON_MODIFIABLE_MENU_ITEMS.includes(key)) {
            return;
        }

        setMenuItems(prev =>
            prev.map(item =>
                item.key === key
                    ? { ...item, enabled: !item.enabled, isOverridden: true }
                    : item
            )
        );
    };

    const handleSaveOverride = async () => {
        setIsSaving(true);
        try {
            // Converte l'array in oggetto { key: boolean }
            // Assicurati che i menu items non modificabili siano sempre enabled
            const configObject = menuItems.reduce((acc, item) => {
                // Forza enabled per items non modificabili
                acc[item.key] = NON_MODIFIABLE_MENU_ITEMS.includes(item.key) ? true : item.enabled;
                return acc;
            }, {} as Record<string, boolean>);

            await apiClient<{ items: Record<string, boolean> }>(
                `/menu-configurations/company/${companyId}`,
                {
                    method: 'POST',
                    requireAuth: true,
                    body: JSON.stringify({
                        items: configObject
                    })
                }
            );

            setHasOverride(true);
            toast.success(t('menuConfig.override.saveSuccess'));

            // Ricarica per aggiornare lo stato
            await loadMenuConfiguration();
        } catch (error) {
            console.error('Error saving menu override:', error);
            toast.error(t('menuConfig.override.saveError'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteOverride = async () => {
        if (!confirm(t('menuConfig.override.deleteConfirm'))) {
            return;
        }

        setIsSaving(true);
        try {
            await apiClient(
                `/menu-configurations/company/${companyId}`,
                {
                    method: 'DELETE',
                    requireAuth: true
                }
            );

            setHasOverride(false);
            toast.success(t('menuConfig.override.deleteSuccess'));

            // Ricarica per mostrare la config globale
            await loadMenuConfiguration();
        } catch (error) {
            console.error('Error deleting menu override:', error);
            toast.error(t('menuConfig.override.deleteError'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        loadMenuConfiguration();
    };

    if (!companyType) {
        return null; // Non mostrare se non c'è un company type
    }

    return (
        <div className="mt-8 rounded-lg border border-secondary bg-primary p-6">
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-primary">
                        {t('menuConfig.override.title')}
                    </h3>
                    <p className="text-sm text-tertiary mt-1">
                        {hasOverride
                            ? t('menuConfig.override.descriptionHasOverride')
                            : t('menuConfig.override.descriptionNoOverride')}
                    </p>
                </div>
                {hasOverride && (
                    <Button
                        color="secondary-destructive"
                        size="sm"
                        onClick={handleDeleteOverride}
                        disabled={isSaving}
                        iconLeading={Trash01}
                    >
                        {t('menuConfig.override.deleteButton')}
                    </Button>
                )}
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-8">
                    <div className="text-tertiary">{t('menuConfig.override.loading')}</div>
                </div>
            ) : (
                <>
                    <div className="space-y-3 mb-6">
                        {menuItems.length === 0 ? (
                            <div className="text-center py-8 text-tertiary">
                                {t('menuConfig.override.noItems')}
                            </div>
                        ) : (
                            <>
                                {/* Non-modifiable items (always enabled) - grouped at top */}
                                {menuItems
                                    .filter(item => NON_MODIFIABLE_MENU_ITEMS.includes(item.key))
                                    .map(item => (
                                        <div
                                            key={item.key}
                                            className="flex items-center justify-between py-3 px-4 bg-secondary rounded-lg opacity-75"
                                        >
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-medium text-primary">
                                                        {item.label}
                                                    </p>
                                                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                                        {t('menuConfig.override.alwaysEnabled')}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-tertiary">
                                                    {t('menuConfig.override.key')}: {item.key}
                                                </p>
                                            </div>
                                            <Toggle
                                                isSelected={item.enabled}
                                                onChange={() => handleToggleMenuItem(item.key)}
                                                aria-label={`Toggle ${item.label}`}
                                                isDisabled={true}
                                            />
                                        </div>
                                    ))}

                                {/* Separator */}
                                {menuItems.some(item => !NON_MODIFIABLE_MENU_ITEMS.includes(item.key)) && (
                                    <div className="border-t border-secondary my-4"></div>
                                )}

                                {/* Modifiable items */}
                                {menuItems
                                    .filter(item => !NON_MODIFIABLE_MENU_ITEMS.includes(item.key))
                                    .map(item => (
                                        <div
                                            key={item.key}
                                            className="flex items-center justify-between py-3 px-4 bg-secondary rounded-lg"
                                        >
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-medium text-primary">
                                                        {item.label}
                                                    </p>
                                                    {item.isOverridden && (
                                                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                                            {t('menuConfig.override.badge')}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-tertiary">
                                                    {t('menuConfig.override.key')}: {item.key}
                                                </p>
                                            </div>
                                            <Toggle
                                                isSelected={item.enabled}
                                                onChange={() => handleToggleMenuItem(item.key)}
                                                aria-label={`Toggle ${item.label}`}
                                            />
                                        </div>
                                    ))}
                            </>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                        <Button
                            color="primary"
                            onClick={handleSaveOverride}
                            disabled={isSaving || menuItems.length === 0}
                            iconLeading={Save01}
                            size="md"
                        >
                            {isSaving ? t('menuConfig.override.saving') : t('menuConfig.override.saveButton')}
                        </Button>
                        <Button
                            color="secondary"
                            onClick={handleReset}
                            disabled={isLoading || isSaving}
                            iconLeading={RefreshCw01}
                            size="md"
                        >
                            {t('menuConfig.override.resetButton')}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}

